import { Component, type ErrorInfo, type ReactNode } from 'react';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log detailed stack to the console to help identify render/update loops.
    // This is intentionally verbose for debugging the production-only crash.
    // eslint-disable-next-line no-console
    console.error('Playback App ErrorBoundary caught an error', error);
    // eslint-disable-next-line no-console
    console.error('Component stack:', info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full min-h-screen flex items-center justify-center bg-base-100 text-center px-6">
          <div className="space-y-3 max-w-lg">
            <h1 className="text-2xl font-semibold">Something went wrong</h1>
            <p className="text-base-content/70">
              The player hit an unexpected error. Please refresh the page. If this keeps happening, we’re already
              logging details to investigate.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
